import { useState } from 'react';
import { Color, Size, clockInZone, farPunchLine, fill, formatDateOnly, hoursLabel, pageKindLineText, t, timeSourceLabel, type ApprovalDay, type ApprovalQueueAnswer } from '@bms2/shared';
import { Button, TextInput } from '../../components/controls';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { Modal } from '../../components/modals/Modal';
import { PageModule } from '../../shell/PageModule';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { post, useApi } from '../records/api';
import { line } from '../settings/SettingsShell';
import { RecordAttendanceWindow } from './RecordAttendance';
import { TimeOffApprovals } from './TimeOffApprovals';
import styles from './TimeApproval.module.css';

/** 🔨 Relative to the API's own root, never prefixed with /api (piece 1). */
export const TIME_APPROVALS_PATH = '/time/approvals';
export const TIME_APPROVE_PATH = `${TIME_APPROVALS_PATH}/approve`;
export const TIME_REJECT_PATH = `${TIME_APPROVALS_PATH}/reject`;
/** The page `queue:time-awaiting-approval` opens (piece 12's queue row). */
export const TIME_APPROVAL_ROUTE = '/time/approve';

/**
 * Time Awaiting Approval (piece 34's queue; decisions 1905, 1983; T1200):
 * each worker's submitted day of entries awaiting approval, approved or
 * rejected whole. 🔒 A rejection asks its reason, which the worker reads on My
 * Jobs. 🔨 An approver's own day offers no decision.
 */
export function TimeApprovalPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const zone = useViewerZone();
  const format = useDateFormat();
  const read = useApi<ApprovalQueueAnswer>(TIME_APPROVALS_PATH, apiBaseUrl);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<{ day: ApprovalDay; reason: string } | null>(null);
  // 🔒 Decision 12851: Record Attendance on one worker's day, beside Approve Day.
  const [recording, setRecording] = useState<ApprovalDay | null>(null);
  const [recorded, setRecorded] = useState<string | null>(null);

  const decide = async (path: string, body: Record<string, unknown>): Promise<boolean> => {
    setBusy(true);
    setError(null);
    const outcome = await post<ApprovalQueueAnswer>(path, body, apiBaseUrl);
    setBusy(false);
    if (!outcome.ok) {
      setError(outcome.message);
      return false;
    }
    read.reload();
    return true;
  };
  const reject = async () => {
    if (!rejecting) return;
    if (await decide(TIME_REJECT_PATH, { employmentId: rejecting.day.employmentId, workDate: rejecting.day.workDate, reason: rejecting.reason })) setRejecting(null);
  };

  return (
    <PageModule title={t('timeApproval.title')} subtitle={pageKindLineText({ page: 'named', route: TIME_APPROVAL_ROUTE })} icon="hr">
      <div className={styles.page} data-page="time-approval">
        <p className={styles.hint}>{t('timeApproval.hint')}</p>
        {error ? <p className={styles.problem} role="alert" data-time-approval-error="true">{error}</p> : null}
        {recorded ? <p className={styles.hint} role="status" data-time-approval-recorded="true">{recorded}</p> : null}
        <AsyncStatePanel state={read.state} emptyAction={t('timeApproval.empty')} onRetry={read.reload}>
          {(answer) => (
            <div className={styles.days}>
              {answer.days.length === 0 ? <p className={styles.hint} data-time-approval-empty="true">{t('timeApproval.empty')}</p> : null}
              {answer.days.map((day) => {
                const own = day.employmentId === answer.viewerEmploymentId;
                return (
                  <section key={`${day.employmentId}|${day.workDate}`} className={styles.day} data-time-approval-day={`${day.employmentId}|${day.workDate}`}>
                    <header className={styles.dayHead}>
                      <h2 className={styles.dayTitle}>{line(day.name, '·', formatDateOnly(`${day.workDate}T00:00:00.000Z`, format), '·', hoursLabel(day.hours))}</h2>
                      {own ? (
                        <span className={styles.hint} data-time-approval-own="true">{t('timeApproval.own')}</span>
                      ) : (
                        <span className={styles.actions}>
                          <span data-time-approval-approve="true">
                            <Button size={Size.Small} color={Color.Good} text={[t('timeApproval.approve')]} disabled={busy} actions={[() => void decide(TIME_APPROVE_PATH, { employmentId: day.employmentId, workDate: day.workDate })]} />
                          </span>
                          <span data-time-approval-reject="true">
                            <Button size={Size.Small} color={Color.Warning} text={[t('timeApproval.reject')]} disabled={busy} actions={[() => setRejecting({ day, reason: '' })]} />
                          </span>
                          <span data-time-approval-attendance="true">
                            <Button size={Size.Small} color={Color.Inactive} text={[t('hr.attendance.record')]} disabled={busy} actions={[() => { setRecorded(null); setRecording(day); }]} />
                          </span>
                        </span>
                      )}
                    </header>
                    {/* 🔒 Decision 12931: how far a gate punch stood from the site's pin, said to the approver alone and never blocking the day. */}
                    {(day.farPunches ?? []).map((punch) => (
                      <p key={punch.entryId} className={styles.hint} data-time-approval-far={punch.entryId}>{farPunchLine(punch)}</p>
                    ))}
                    <ul className={styles.entries}>
                      {day.entries.map((entry) => (
                        <li key={entry.id} className={styles.entry} data-time-approval-entry={entry.id}>
                          <span>{line(entry.workOrderNumber, entry.lineLabel ? `· ${entry.lineLabel}` : entry.entryType === 'Travel' ? `· ${t('timeApproval.travel')}` : `· ${t('timeApproval.noLine')}`)}</span>
                          <span className={styles.hint}>{line(timeSourceLabel(entry.captureSource), entry.startedAt ? clockInZone(entry.startedAt, zone) : null, entry.endedAt ? `– ${clockInZone(entry.endedAt, zone)}` : null)}</span>
                          <span>{hoursLabel(entry.hours)}</span>
                          {entry.note ? <span className={styles.hint}>{entry.note}</span> : null}
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}
        </AsyncStatePanel>
        {/* 🔒 Decision 13030: time-off requests are decided here too, read by their own route, which asks no time.approve. */}
        <TimeOffApprovals apiBaseUrl={apiBaseUrl} />
      </div>
      {/* 🔨 Mounted only while open and keyed to the day, so a second day's window never shows the first one's draft. */}
      {recording ? (
        <RecordAttendanceWindow
          key={`${recording.employmentId}|${recording.workDate}`}
          apiBaseUrl={apiBaseUrl}
          day={recording}
          onClose={() => setRecording(null)}
          onRecorded={() => {
            setRecorded(fill(t('hr.attendance.recorded'), { name: recording.name }));
            setRecording(null);
          }}
        />
      ) : null}
      <Modal
        open={rejecting !== null}
        title={t('timeApproval.rejectTitle')}
        {...(rejecting ? { subtitle: line(rejecting.day.name, '·', formatDateOnly(`${rejecting.day.workDate}T00:00:00.000Z`, format)) } : {})}
        onCancel={() => setRejecting(null)}
        dataModal="time-approval-reject"
        actions={[
          { kind: 'primary', label: t('timeApproval.reject'), disabled: busy || !rejecting?.reason.trim(), onAct: () => void reject() },
          { kind: 'cancel', label: t('modal.cancel'), onAct: () => setRejecting(null) },
        ]}
      >
        <p className={styles.hint}>{t('timeApproval.reasonHint')}</p>
        <TextInput size={Size.Large} label={t('timeApproval.reason')} value={rejecting?.reason ?? ''} onChange={(reason) => setRejecting(rejecting ? { ...rejecting, reason } : null)} name="time-approval-reason" required />
      </Modal>
    </PageModule>
  );
}
